import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import { motion } from "framer-motion";
import { login, authFetch, fetchSiteIdentity, fetchServices } from "../api";
import defaultLogo from "../assets/logo.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const [siteTitle, setSiteTitle] = useState("Mr. Aali Online Technologies");
  const [siteTagline, setSiteTagline] = useState("Empowering Businesses Digitally");
  const [services, setServices] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSiteIdentity()
      .then((identity) => {
        if (identity.logo) setLogoUrl(identity.logo);
        setSiteTitle(identity.site_title);
        setSiteTagline(identity.site_tagline);
      })
      .catch(() => {});
    fetchServices().then(setServices).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const res = await authFetch("/core/profile/");
      const profile = await res.json();
      if (profile.is_staff) return navigate("/admin/dashboard");
      // Co-Work buyers / sellers land on their marketplace dashboard; everyone else on the home page.
      const me = await authFetch("/core/cowork/me/").then((r) => (r.ok ? r.json() : null)).catch(() => null);
      navigate(me && me.role ? "/co-work/dashboard" : "/");
    } catch (err) {
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-navy-darker text-white">
      {/* Left column — branding + services */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-navy border-r border-navy-border flex-col justify-center px-16">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" />
          <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent-light/10 blur-[120px]" />
        </div>

        <div className="relative z-10">
          <Link to="/">
            <img src={logoUrl} alt={siteTitle} className="h-12 mb-8" />
          </Link>
          <h1 className="text-4xl font-extrabold tracking-tight mb-3">{siteTitle}</h1>
          <p className="text-lg text-gray-300 mb-10">{siteTagline}</p>

          {services.length > 0 && (
            <div>
              <p className="text-sm uppercase tracking-widest text-accent-light font-semibold mb-4">
                What We Do
              </p>
              <ul className="space-y-3">
                {services.slice(0, 6).map((service) => (
                  <li key={service.id} className="flex items-center gap-3 text-gray-200">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent/15 text-accent-light shrink-0">
                      <FiCheck size={14} />
                    </span>
                    {service.title}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Right column — login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="lg:hidden flex justify-center mb-8">
            <img src={logoUrl} alt={siteTitle} className="h-10" />
          </Link>

          <h2 className="text-2xl font-bold mb-1">Welcome back</h2>
          <p className="text-sm text-gray-400 mb-8">
            Sign in to your account — for both clients and team members.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light transition-colors"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-brand-gradient text-white font-semibold shadow-md shadow-accent/20 disabled:opacity-60 transition-shadow"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="text-sm text-gray-500 text-center mt-8">
            <Link to="/" className="hover:text-gray-300 transition-colors">
              &larr; Back to Home
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
